/* eslint-disable @typescript-eslint/no-explicit-any */
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import DoneIcon from '@mui/icons-material/Done';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import {
  addDays,
  addMonths,
  addYears,
  format,
  formatISO,
  fromUnixTime,
  getUnixTime,
  isFuture,
  subDays,
} from 'date-fns';
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Bar, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { ButtonFilled } from '../../../components/Button/Button';
import DialogBox from '../../../components/DialogBox/DialogBox';
import FormField from '../../../components/FormField/FormField';
import LocationTagFilter from '../../../components/LocationTagFilter/LocationTagFilter';
import HamburgerIcon from '../../../components/HamburgerIcon/HamburgerIcon';
import { DataContext } from '../../../context/DataContext';
import { useLocationContext } from '../../../context/LocationContext';
import backgroundImage from '../../../images/FaintBackground.jpg';
import {
  DateToISO,
  deleteData,
  downloadURI,
  getAccountId,
  getData,
  patchData,
  postData,
  randomRGB,
  returnBackToLogin,
  StyledTableCell,
  StyledTableRow,
} from '../../../utils/utils';
import './Dashboard.scss';
import {
  barChartOptions,
  firstTab,
  lineChartOptions,
  secondTab,
  stackedBarChartOptions,
  stackedBarChartPlugins,
} from './data';
import { defaultChart, defaultReport } from './defaults';
import FirstLine from './FirstLine';
import SecondLine from './SecondLine';
import { DashboardDataContext } from '../../../context/DashboardDataContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Scriptable Chart.js borderRadius for the stacked activity chart. Rounds only
// the outer edges of each column — the top of the topmost segment and the
// bottom of the bottommost segment that actually has a value — so the whole
// stack reads as one rounded bar instead of trying (and failing) to round every
// tiny segment. Middle segments stay square. Chart.js clamps the radius to the
// segment's height/width, so slivers never distort.
const STACK_CORNER_RADIUS = 8;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stackCornerRadius = (context: any) => {
  const { chart, datasetIndex, dataIndex } = context;
  const datasets = chart?.data?.datasets || [];
  const stackOrder = datasets
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((dataset: any, index: number) => ({
      index,
      value: Number(dataset.data?.[dataIndex]) || 0,
    }))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((entry: any) => entry.value > 0)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((entry: any) => entry.index);
  if (!stackOrder.length) return 0;
  const isBottom = stackOrder[0] === datasetIndex;
  const isTop = stackOrder[stackOrder.length - 1] === datasetIndex;
  if (!isBottom && !isTop) return 0;
  return {
    topLeft: isTop ? STACK_CORNER_RADIUS : 0,
    topRight: isTop ? STACK_CORNER_RADIUS : 0,
    bottomLeft: isBottom ? STACK_CORNER_RADIUS : 0,
    bottomRight: isBottom ? STACK_CORNER_RADIUS : 0,
  };
};

// The graph palette is built ONLY by sampling this teal → green → blue → purple
// gradient (sampled from the brand gradient bar). Every bar colour is a point
// along this gradient, so no colour ever falls outside it.
const GRADIENT_STOPS = [
  [72, 188, 178], // #48BCB2
  [51, 183, 161], // #33B7A1
  [50, 168, 162], // #32A8A2
  [50, 152, 157], // #32989D
  [46, 127, 153], // #2E7F99
  [54, 105, 149], // #366995
  [68, 86, 144], // #445690
  [80, 63, 143], // #503F8F
  [82, 47, 103], // #522F67
];

// Sample the gradient at position t (0 = light teal, 1 = purple), linearly
// interpolating between the two surrounding stops. Returns an "r,g,b" string.
const sampleGradient = (t: number): string => {
  const clamped = Math.min(Math.max(t, 0), 1);
  const scaled = clamped * (GRADIENT_STOPS.length - 1);
  const lower = Math.floor(scaled);
  const upper = Math.min(lower + 1, GRADIENT_STOPS.length - 1);
  const frac = scaled - lower;
  return GRADIENT_STOPS[lower]
    .map((channel, i) => Math.round(channel + (GRADIENT_STOPS[upper][i] - channel) * frac))
    .join(',');
};

// Colour generator: gives every item (location or activity) a colour sampled
// from the gradient, spreading the N items evenly from light teal (item 0) to
// purple (last item) so they are as far apart on the gradient as possible and
// no two are identical within one selection. Kept as brandColour(index, count)
// so all call sites are unchanged.
const brandColour = (index: number, count: number): string => {
  const safeIndex = index < 0 ? 0 : index;
  const t = count > 1 ? safeIndex / (count - 1) : 0;
  return sampleGradient(t);
};

// Card and button chrome shared by the toolbar, the chart cards and the
// Location Breakdown card so every surface on the dashboard has the same edges.
const cardSx = {
  backgroundColor: '#fff',
  border: '1px solid #e6eaeb',
  borderRadius: '12px',
};

const toolbarButtonSx = {
  border: '1px solid #d5dbdd',
  borderRadius: '8px',
  padding: '7px 14px',
  fontSize: '12.5px',
  fontWeight: 600,
  letterSpacing: '.4px',
  textTransform: 'uppercase' as const,
  color: 'rgb(100,100,100)',
  '&:hover': { borderColor: '#61b9c3', color: '#4a9aa3' },
};

// "5120.95" -> "5,120.95" for the total under each chart.
const formatTonnes = (value: string) => {
  const parsed = Number(value);
  if (!value || Number.isNaN(parsed)) return value;
  return parsed.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// The Location Breakdown table opts out of the app-wide teal StyledTableCell
// head in favour of the plain uppercase header used across this dashboard.
const breakdownHeadSx = {
  textTransform: 'uppercase' as const,
  fontSize: '11.5px',
  letterSpacing: '.6px',
  color: '#9aa3a7',
  fontWeight: 600,
  padding: '0 10px 9px',
  borderBottom: '1px solid #e6eaeb',
};

const breakdownCellSx = {
  fontSize: '13.5px',
  padding: '10px',
  borderBottom: '1px solid #e6eaeb',
};

const toolbarPrimaryButtonSx = {
  backgroundColor: '#61b9c3',
  border: '1px solid #61b9c3',
  borderRadius: '8px',
  padding: '7px 14px',
  fontSize: '12.5px',
  fontWeight: 600,
  letterSpacing: '.4px',
  textTransform: 'uppercase' as const,
  color: '#fff',
  '&:hover': { backgroundColor: '#4a9aa3', borderColor: '#4a9aa3' },
};

const defaultNewReportErrors = {
  nameError: '',
  descriptionError: '',
  expiryError: '',
};

function Dashboard() {
  const navigate = useNavigate();
  const lineChartRef = useRef<any>(null);
  const barChartRef = useRef<any>(null);

  const { setLoading, setMessagebar } = useContext(DataContext);
  const { locations } = useLocationContext();
  const [chartData, setChartData] = useState(defaultChart);
  const [perOrganisationBreakdown, setPerOrganisationBreakdown] = useState<
    {
      organisation_id: string;
      name: string;
      total_emissions: number;
      activity_emissions?: { [activityLabel: string]: number };
    }[]
  >([]);
  const [totalEmissionActivityChart, setTotalEmissionActivityChart] = useState('');
  const [totalEmissionTimeSeries, setTotalEmissionTimeSeries] = useState('');
  const [orgActivityStartDate, setOrgActivityStartDate] = useState(new Date());
  const [firstTabLinks, setFirstTabLinks] = useState(firstTab);
  const [secondTabLinks, setSecondTabLinks] = useState(secondTab);
  const [reportTypes, setReportTypes] = useState(['emissions']);
  const [chartType, setChartType] = useState('activityChart');

  const {
    calenderStartDate,
    setCalanderStartDate,
    calenderEndDate,
    setCalanderEndDate,
    calanderStartDateEditMode,
    setCalanderStartDateEditMode,
    calanderEndDateEditMode,
    setCalanderEndDateEditMode,
  } = useContext(DashboardDataContext);

  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState({ id: '' });
  const [reportEditMode, setReportEditMode] = useState(defaultReport);
  const [reportDownloadMode, setReportDownloadMode] = useState(defaultReport);
  const [firstTabLinksEditMode, setFirstTabLinksEditMode] = useState(firstTab);
  const [reportTypesEditMode, setReportTypesEditMode] = useState(['emissions']);
  const [chartTypeEditMode, setChartTypeEditMode] = useState('activityChart');
  const [chartKey, setChartKey] = useState(0);
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const isOverallTotal = selectedOrganisation === 'overall'
    || Cookies.get('selectedOrganisation') === 'overall';

  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [appliedTags, setAppliedTags] = useState<string[]>([]);
  // Location whose segments stay lit in the activity chart while every other
  // location dims. Toggled by clicking a Location Breakdown row.
  const [isolatedLocationId, setIsolatedLocationId] = useState<string | null>(null);

  const [sliderValue, setSliderValue] = useState<number[]>([
    getUnixTime(orgActivityStartDate),
    getUnixTime(new Date(format(new Date(), 'yyyy-MM-dd'))),
  ]);

  const [showWelcome, setShowWelcome] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showCreateNewReport, setShowCreateNewReport] = useState(false);
  const [showEditReport, setShowEditReport] = useState(false);
  const [showDownloadReport, setShowDownloadReport] = useState(false);
  const [showGenerateCode, setShowGenerateCode] = useState(false);
  const [showShareCode, setShowShareCode] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newReportValues, setNewReportValues] = useState({
    name: '',
    description: '',
    expiry: new Date(),
  });
  const [shareCodeExpiry, setShareCodeExpiry] = useState(new Date());
  const [newShareCode, setNewShareCode] = useState('');
  const ifAggregate = false;

  const handleSliderLabel = (value?: any) => {
    const dateToDisplayOnSlider = format(fromUnixTime(value), 'dd/MM/yyyy');
    return `${dateToDisplayOnSlider}`;
  };

  const handleSliderChange = async (event: Event, newValue: any) => {
    setCalanderStartDate(fromUnixTime(newValue[0]));
    setCalanderEndDate(fromUnixTime(newValue[1]));
    setSliderValue(newValue);
    setSecondTabLinks(secondTab);
  };

  // "Overall Total" isn't a real organisation id and the `active_org_session`
  // cookie gets overwritten to the top-level parent org id once Overall Total is
  // selected (see Sidebar.tsx) — that id is rejected by the reports API, which
  // only accepts a child-entity id. The account's `active_org_session` field,
  // however, always stays a valid child-entity id (switching entities doesn't
  // update it - see toggleSwitchOrg's `change_active_session: false`), so fetch
  // it fresh from the account instead of trusting the cookie. Every place that
  // lists/creates reports for the current selection must go through this helper.
  const getStableEntityId = async () => {
    const accountId = getAccountId();
    const currentUserResponse = accountId
      ? await getData(`/accounts/${accountId.accountId}`)
      : null;
    return currentUserResponse?.data?.active_org_session;
  };

  const getReportListUrl = async () => {
    if (!isOverallTotal) {
      return `/reports/organisation/${selectedOrganisation}?multiOrgReport=false`;
    }
    const entityId = await getStableEntityId();
    return `/reports/organisation/${entityId}?multiOrgReport=true`;
  };

  const handleDownloadbutton = async () => {
    setLoading(true);
    try {
      const listUrl = await getReportListUrl();
      const getReport = await getData(listUrl);
      setLoading(false);
      setReports(Array.isArray(getReport.data) ? getReport.data : []);
    } catch (error: any) {
      setLoading(false);
      setReports([]);
    }
    setShowDownload(true);
  };

  const processDataForLineChart = (feedData: any) => {
    if (
      !feedData?.chart_data?.chart_data_time_series
      || !feedData.chart_data.chart_data_time_series.labels
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const limitData = (params: any, outputLength: number) => {
      const firstElement = params[0];
      let midElements = params.slice(1, -1);
      midElements = midElements.filter(
        (single: any, index: number) => index % Math.floor(params.length / outputLength) === 0,
      );
      const lastElement = params[params.length - 1];
      return [firstElement, ...midElements, lastElement];
    };

    const bottomLabels = feedData.chart_data.chart_data_time_series.labels;
    const dataToDisplayBeforeFormatting = feedData.chart_data.chart_data_time_series;
    const LIMIT_DATAPOINTS_HORIZONTALLY = 20;

    let labelToDisplayAfterFormatting = bottomLabels;
    if (bottomLabels.length > LIMIT_DATAPOINTS_HORIZONTALLY) {
      labelToDisplayAfterFormatting = limitData(
        bottomLabels,
        LIMIT_DATAPOINTS_HORIZONTALLY,
      );
    }

    const dataToDisplayAfterFormatting = dataToDisplayBeforeFormatting.datasets.map(
      (single: any, index: number) => {
        const { label, data } = single;
        let limitedDataPoints = data;

        if (bottomLabels.length > LIMIT_DATAPOINTS_HORIZONTALLY) {
          limitedDataPoints = limitData(data, LIMIT_DATAPOINTS_HORIZONTALLY);
        }

        let randomColor;
        if (index === 0) {
          // randomColor = randomRGB();
          randomColor = '97,185,195';
          return {
            label,
            data: limitedDataPoints,
            borderColor: `rgb(${randomColor})`,
            backgroundColor: `rgba(${randomColor},0.6)`,
            tension: 0,
            fill: true,
          };
        }
        // randomColor = randomRGB();
        randomColor = '200,200,200';
        return {
          label,
          data: limitedDataPoints,
          borderColor: `rgb(${randomColor})`,
          backgroundColor: `rgba(${randomColor},0.4)`,
          tension: 0,
          fill: true,
        };
      },
    );

    const data = {
      labels: labelToDisplayAfterFormatting,
      datasets: dataToDisplayAfterFormatting,
    };
    return data;
  };

  const updateReport = async () => {
    const body = {
      name: reportEditMode.name,
      description: reportEditMode.description,
      organisation: selectedOrganisation,
      report_types: reportTypesEditMode,
      expiry: reportEditMode.expiry,
      start_date: formatISO(calanderStartDateEditMode),
      end_date: formatISO(calanderEndDateEditMode),
    };
    // Trigger pull new data and setReportEditMode
    await patchData(`/reports/${reportEditMode.id}`, body);
  };

  const processDataForBarChart = (feedData: any) => {
    if (
      !feedData?.chart_data?.chart_data_by_activity
      || !feedData.chart_data.chart_data_by_activity.labels
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const { labels } = feedData.chart_data.chart_data_by_activity;
    const dataToDisplayBeforeFormatting = feedData.chart_data.chart_data_by_activity;

    const dataToDisplayAfterFormatting = dataToDisplayBeforeFormatting.datasets.map(
      (single: any, index: number) => {
        let randomColor = randomRGB();
        if (index === 0) {
          // Every activity gets its own unique, stable colour from the brand
          // generator — no fixed list to run out of or repeat.
          const colors: string[] = [];
          for (
            let labelIndex = 0;
            labelIndex < labels.length;
            labelIndex += 1
          ) {
            colors.push(`rgba(${brandColour(labelIndex, labels.length)},0.9)`);
          }
          return {
            ...single,
            borderColor: `rgb(${randomColor})`,
            backgroundColor: colors,
            tension: 0.4,
            fill: true,
            // Match the overall stacked chart: rounded bars and the same
            // spacing so the single-location view reads as the same design.
            borderRadius: 8,
            borderSkipped: false as const,
            barThickness: 'flex' as const,
            maxBarThickness: 58,
            categoryPercentage: 0.72,
            barPercentage: 0.92,
          };
        }
        randomColor = '200,200,200';
        return {
          ...single,
          borderColor: `rgb(${randomColor})`,
          backgroundColor: `rgba(${randomColor},0.6)`,
          tension: 0.4,
          fill: true,
          borderRadius: 8,
          borderSkipped: false as const,
          barThickness: 'flex' as const,
          maxBarThickness: 58,
          categoryPercentage: 0.72,
          barPercentage: 0.92,
        };
      },
    );
    const data = { labels, datasets: dataToDisplayAfterFormatting };
    return data;
  };

  const handleSetStartDate = (params: any) => {
    if (params < orgActivityStartDate) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: `Please choose a date later than ${orgActivityStartDate}`,
        severity: 'error',
      }));
      return;
    }
    if (params > new Date()) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Start date can not be today or in the future',
        severity: 'error',
      }));
      return;
    }
    if (params > calenderEndDate) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Start date can not be after end date',
        severity: 'error',
      }));
      return;
    }
    setSliderValue([getUnixTime(params), getUnixTime(calenderEndDate)]);
    setCalanderStartDate(params);
  };

  const handleSetStartDateEditMode = (params: any) => {
    if (params < orgActivityStartDate) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: `Please choose a date later than ${orgActivityStartDate}`,
        severity: 'error',
      }));
      return;
    }
    if (params > new Date()) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Start date can not be today or in the future',
        severity: 'error',
      }));
      return;
    }
    if (params > calanderEndDateEditMode) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Start date can not be after end date',
        severity: 'error',
      }));
      return;
    }
    setCalanderStartDateEditMode(params);

    updateReport();

    // now setReportEditMode !!
  };

  const handleSetEndDate = (params: any) => {
    if (params > new Date()) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'End Date can not be in the future',
        severity: 'error',
      }));
      return;
    }
    if (params < calenderStartDate) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'End date can not be before start date',
        severity: 'error',
      }));
      return;
    }

    setCalanderEndDate(params);
  };

  const handleSetEndDateEditMode = (params: any) => {
    if (params > new Date()) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'End Date can not be in the future',
        severity: 'error',
      }));
      return;
    }
    if (params < calanderStartDateEditMode) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'End date can not be before start date',
        severity: 'error',
      }));
      return;
    }

    setCalanderEndDateEditMode(params);
    updateReport();
    // now setReportEditMode
  };

  const handleFirstTabLinks = (params?: any) => {
    if (params === 'emissions') {
      setReportTypes(['emissions']);
    } else if (params === 'benchmarks') {
      setReportTypes((prev) => [...prev, 'benchmarks']);
    }

    const alteredData = firstTabLinks.map((single) => (single.value !== params
      ? { ...single, active: false }
      : { ...single, active: true }));
    setFirstTabLinks(alteredData);
  };

  const handleFirstTabLinksEditMode = (params?: any) => {
    if (params === 'emissions') {
      setReportTypesEditMode(['emissions']);
    } else if (params === 'benchmarks') {
      setReportTypesEditMode((prev) => [...prev, 'benchmarks']);
    }

    const alteredData = firstTabLinksEditMode.map((single) => (single.value !== params
      ? { ...single, active: false }
      : { ...single, active: true }));

    setFirstTabLinksEditMode(alteredData);
    updateReport();
    // now setReportEditMode
  };

  const handleSecondTabLinks = (params?: any) => {
    setCalanderStartDate(orgActivityStartDate);

    if (params === 'threeYears') {
      const dateAfterThreeYears = addYears(orgActivityStartDate, 3);
      if (isFuture(dateAfterThreeYears)) {
        // setMessagebar((prev: any) => ({
        //   ...prev,
        //   show: true,
        //   text: "Not enough activities for 3 years",
        //   severity: "success",
        // }));
        setSliderValue([
          getUnixTime(orgActivityStartDate),
          getUnixTime(dateAfterThreeYears),
        ]);
        setCalanderEndDate(new Date());
        const alteredData = secondTabLinks.map((single) => ({
          ...single,
          active: false,
        }));
        setSecondTabLinks(alteredData);
        return;
      }
      setSliderValue([
        getUnixTime(orgActivityStartDate),
        getUnixTime(dateAfterThreeYears),
      ]);
      setCalanderEndDate(dateAfterThreeYears);
    } else if (params === 'yearly') {
      const dateAfterOneYear = addYears(orgActivityStartDate, 1);
      if (isFuture(dateAfterOneYear)) {
        // setMessagebar((prev: any) => ({
        //   ...prev,
        //   show: true,
        //   text: "Not enough activities for 1 year",
        //   severity: "success",
        // }));
        setSliderValue([
          getUnixTime(orgActivityStartDate),
          getUnixTime(dateAfterOneYear),
        ]);
        setCalanderEndDate(new Date());
        const alteredData = secondTabLinks.map((single) => ({
          ...single,
          active: false,
        }));
        setSecondTabLinks(alteredData);
        return;
      }
      setSliderValue([
        getUnixTime(orgActivityStartDate),
        getUnixTime(dateAfterOneYear),
      ]);
      setCalanderEndDate(dateAfterOneYear);
    } else if (params === 'monthly') {
      const dateAfterOneMonth = addMonths(orgActivityStartDate, 1);
      if (isFuture(dateAfterOneMonth)) {
        // setMessagebar((prev: any) => ({
        //   ...prev,
        //   show: true,
        //   text: "Not enough activities for 1 month",
        //   severity: "success",
        // }));
        setSliderValue([
          getUnixTime(orgActivityStartDate),
          getUnixTime(dateAfterOneMonth),
        ]);
        setCalanderEndDate(new Date());
        const alteredData = secondTabLinks.map((single) => ({
          ...single,
          active: false,
        }));
        setSecondTabLinks(alteredData);
        return;
      }
      setSliderValue([
        getUnixTime(orgActivityStartDate),
        getUnixTime(dateAfterOneMonth),
      ]);
      setCalanderEndDate(dateAfterOneMonth);
    }

    const alteredData = secondTabLinks.map((single) => (single.value !== params
      ? { ...single, active: false }
      : { ...single, active: true }));
    setSecondTabLinks(alteredData);
  };

  const handleChartType = (params: string) => {
    setChartType(params);
  };

  const handleChartTypeEditMode = (params: string) => {
    setChartTypeEditMode(params);
  };

  const handleCreateNewReport = () => {
    setShowShare(false);
    setShowDownload(false);
    setShowCreateNewReport(true);
  };

  const handleSubmitCreateNewReport = async (e: any) => {
    e.preventDefault();

    if (newReportValues.name === '' || newReportValues.description === '') {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Name and/or Description can not be empty',
        severity: 'error',
      }));
      return;
    }

    setShowCreateNewReport(false);
    setLoading(true);
    try {
      // "Overall Total" has no real single organisation id to create a report
      // against, so it must go through the multi-org-report endpoint instead,
      // covering whichever locations/tags are currently selected in the
      // Location Breakdown filter (falling back to every location).
      let createResponse: any;
      if (isOverallTotal) {
        const entityId = await getStableEntityId();
        const groupLocationIds = selectedLocationIds;
        const body = {
          name: newReportValues.name,
          description: newReportValues.description,
          organisation: entityId,
          report_types: reportTypes,
          expiry: '2099-12-31T00:00:00+00:00',
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: true,
          multi_org_reports: true,
          multi_org_report_group_id: groupLocationIds.length > 0
            ? groupLocationIds
            : locations.map((loc) => loc.id),
        };
        createResponse = await postData('/reports/create-multi-org-report', body);
      } else {
        const body = {
          name: newReportValues.name,
          description: newReportValues.description,
          organisation: selectedOrganisation,
          report_types: reportTypes,
          expiry: '2099-12-31T00:00:00+00:00',
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: ifAggregate,
          multi_org_reports: false,
          multi_org_report_group_id: selectedOrganisation,
        };
        createResponse = await postData('/reports', body);
      }

      if (createResponse.status !== 201) {
        setLoading(false);
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: `${createResponse.data || 'Unable to create report'}`,
          severity: 'error',
        }));
        return;
      }

      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Report successfully created',
        severity: 'success',
      }));
      const listUrl = await getReportListUrl();
      const getReports = await getData(listUrl);
      setReports(Array.isArray(getReports.data) ? getReports.data : []);
      setShowDownload(true);

      setLoading(false);
      setNewReportValues({
        name: '',
        description: '',
        expiry: new Date(),
      });
    } catch (error: any) {
      setLoading(false);
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Some error occurred. Please try again.',
        severity: 'error',
      }));
    }
  };

  const shareAReport = async (params: any) => {
    setReportEditMode(params);
    setShowShare(false);
    setShowGenerateCode(true);
  };

  const handleCreateShareCode = async (e: any) => {
    e.preventDefault();
    const body = {
      report_id: reportEditMode.id,
      code_expiry: shareCodeExpiry.toISOString(),
    };
    setLoading(true);
    try {
      const getShareCode = await postData('/reports/code/generate', body);
      setNewShareCode(getShareCode.data);
      setLoading(false);
      setShowGenerateCode(false);
      setShowShareCode(true);
    } catch (error: any) {
      setLoading(false);
    }
  };

  const confirmDeleteAReport = (report: any) => {
    setSelectedReport(report);
    setShowConfirmDialog(true);
  };

  const deleteAReport = async () => {
    setLoading(true);
    try {
      const response = await deleteData(`/reports/${selectedReport.id}`);
      if (response.status === 204) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Report successfully deleted',
          severity: 'success',
        }));

        const listUrl = await getReportListUrl();
        const getReport = await getData(listUrl);
        setReports(Array.isArray(getReport.data) ? getReport.data : []);
        setShowConfirmDialog(false);
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
    }
  };

  const editAReport = (params: any) => {
    setShowShare(false);
    setShowDownload(false);
    setShowEditReport(true);
    setReportEditMode(params);
  };

  const cancelEditReport = () => {
    setShowEditReport(false);
    setShowDownload(false);
  };

  const handleSaveChanges = async () => {
    const body = {
      name: reportEditMode.name,
      description: reportEditMode.description,
      organisation: selectedOrganisation,
      report_types: reportTypesEditMode,
      expiry: reportEditMode.expiry,
      start_date: formatISO(calanderStartDateEditMode),
      end_date: formatISO(calanderEndDateEditMode),
    };
    // Trigger udpate the reportEditMode with new changes
    setLoading(true);
    await patchData(`/reports/${reportEditMode.id}`, body);
    setLoading(false);
    setShowEditReport(false);
  };

  const downloadAReport = (params: any) => {
    setShowShare(false);
    setShowDownload(false);
    setShowDownloadReport(true);
    setReportDownloadMode(params);
  };

  const cancelDownloadReport = () => {
    setShowDownloadReport(false);
    setShowDownload(true);
  };

  const createReportPDF = async () => {
    const currentUserResponse = await getData(
      `/accounts/${reportDownloadMode.created_by}`,
    );
    if (currentUserResponse.status !== 200) {
      return;
    }

    const currentUser = currentUserResponse.data;
    const startDateISO = DateToISO(reportDownloadMode.start_date);
    const endDateISO = DateToISO(reportDownloadMode.end_date);

    const payload = {
      report_id: reportDownloadMode.id,
      organisation: currentUser.active_org_session,
      aggregate: true,
      report_types: ['emissions'],
      start_date: startDateISO,
      end_date: endDateISO,
      // Must match the `multi_org_report` flag stored on the saved report row
      // (see downloadInstantReport's exact-match lookup), otherwise the backend
      // can't find it and the download silently fails.
      multi_org_reports: isOverallTotal,
    };

    let reportDataResponse = null;
    try {
      setLoading(true);
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Report is being generated. Please wait...',
        severity: 'warning',
      }));

      if (reportDownloadMode.id) {
        reportDataResponse = await postData('/reports/download-instant', payload);
      } else {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Unable to download report',
          severity: 'error',
        }));
        setLoading(false);
        return;
      }
      setLoading(false);
    } catch (e) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Unable to download report',
        severity: 'error',
      }));
      setLoading(false);
      return;
    }

    if (!reportDataResponse || reportDataResponse?.status !== 200) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Unable to download report',
        severity: 'error',
      }));
    }

    const { data } = reportDataResponse;
    const fileName = data.split('filename=')[1].split(';')[0];
    downloadURI(data, fileName);
    setReportDownloadMode(defaultReport);
  };

  const downloadNow = () => {
    setShowDownloadReport(false);
    createReportPDF();
  };

  // Track if initial load has been completed
  const initialLoadRef = useRef(false);

  useEffect(() => {
    // Prevent running if already initialized
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    setLoading(true);

    const fetchData = async () => {
      const accountId = getAccountId();
      if (!accountId) return;
      const currentUser = await getData(`/accounts/${accountId.accountId}`);

      // Only set selected org from user if cookie is missing
      const selectedOrgFromCookie = Cookies.get('selectedOrganisation');
      const userOrg = currentUser?.data?.active_org_session ?? '';

      if (!selectedOrgFromCookie && userOrg) {
        Cookies.set('selectedOrganisation', userOrg);
        Cookies.set('active_org_session', userOrg);
      }

      const orgIdToUse = selectedOrgFromCookie || userOrg;

      if (!orgIdToUse) {
        returnBackToLogin();
        return;
      }

      const isOverall = orgIdToUse === 'overall';

      // Don't rely on LocationContext's `locations` here — it loads asynchronously
      // in a separate component and can still be `[]` on a refresh even for a user
      // who genuinely has multiple locations, which would wrongly trigger the
      // "Welcome to your dashboard" first-time-user popup below. Fetch the entity
      // list directly (same endpoint LocationContext itself uses) so this check is
      // accurate regardless of that context's load timing.
      let locationIds: string[] = [];
      if (isOverall) {
        const entitiesResponse = await getData(
          `/entities/?organisation_id=${currentUser?.data?.organisation}&is_archive=false`,
        );
        locationIds = (entitiesResponse.data || []).map((loc: any) => loc.id);
      }

      if (isOverall && locationIds.length === 0) {
        setOrgActivityStartDate(new Date());
        setShowWelcome(true);
        setLoading(false);
        setSelectedOrganisation(orgIdToUse);
        return;
      }

      if (!isOverall && !orgIdToUse) {
        setOrgActivityStartDate(new Date());
        setShowWelcome(true);
        setLoading(false);
        setSelectedOrganisation(orgIdToUse);
        return;
      }

      // Get Organisation Start date - for Overall Total, use the earliest
      // start date across every location (only one needs to have data).
      let earliestStartDate: Date | null = null;
      if (isOverall) {
        const startDateResponses = await Promise.all(
          locationIds.map((id) => getData(`/reports/startdate/${id}`)),
        );
        startDateResponses.forEach((res) => {
          if (res.status !== 400) {
            const candidate = new Date(res.data);
            if (!earliestStartDate || candidate < earliestStartDate) {
              earliestStartDate = candidate;
            }
          }
        });
      } else {
        const getOrgActivityStartDate = await getData(
          `/reports/startdate/${orgIdToUse}`,
        );
        if (getOrgActivityStartDate.status !== 400) {
          earliestStartDate = new Date(getOrgActivityStartDate.data);
        }
      }

      if (!earliestStartDate) {
        setOrgActivityStartDate(new Date());
        setShowWelcome(true);
        setLoading(false);
        setSelectedOrganisation(orgIdToUse);
        return;
      }
      setOrgActivityStartDate(earliestStartDate);

      // Set Chart data
      const body = isOverall
        ? {
          organisation: locationIds,
          report_types: reportTypes,
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: true,
          use_currency_conversion: true,
        }
        : {
          organisation: orgIdToUse,
          report_types: reportTypes,
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: ifAggregate,
          use_currency_conversion: true,
        };
      // Belt-and-suspenders: never call the multi-org endpoint with an empty
      // organisation array, regardless of what the guards above determined.
      if (isOverall && body.organisation.length === 0) {
        setLoading(false);
        setSelectedOrganisation(orgIdToUse);
        return;
      }

      const response = await postData(
        isOverall ? '/reports/instant-multi-report' : '/reports/instant',
        body,
      );

      if (response.status !== 201) {
        setLoading(false);
        setSelectedOrganisation(orgIdToUse);
        return;
      }
      setChartData(response.data);
      setPerOrganisationBreakdown(response.data.per_organisation || []);
      setSliderValue([
        getUnixTime(
          new Date(format(new Date(calenderStartDate), 'yyyy-MM-dd')),
        ),
        getUnixTime(new Date(format(new Date(), 'yyyy-MM-dd'))),
      ]);
      setLoading(false);
      // Set organisation AFTER all data is loaded to prevent triggering the second useEffect
      setSelectedOrganisation(orgIdToUse);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Skip if no organisation selected or if this is the initial load
      if (!selectedOrganisation || !initialLoadRef.current) return;

      const isOverall = selectedOrganisation === 'overall';
      // On a hard refresh, LocationContext's location list can still be loading
      // asynchronously when this effect fires. Sending an empty organisation
      // array to the multi-org endpoint gets rejected with a raw 400 ("organisation
      // must be a non-empty array"), which then surfaces as a confusing error
      // message. Skip silently here — once locations finish loading, the
      // Location Breakdown filter's own effect re-fetches with the real list.
      if (isOverall && locations.length === 0) return;

      const body = isOverall
        ? {
          organisation: locations.map((loc) => loc.id),
          report_types: reportTypes,
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: true,
          use_currency_conversion: true,
        }
        : {
          organisation: selectedOrganisation,
          report_types: reportTypes,
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: ifAggregate,
          use_currency_conversion: true,
        };

      // Belt-and-suspenders: never call the multi-org endpoint with an empty
      // organisation array, regardless of what the guard above determined.
      if (isOverall && body.organisation.length === 0) return;

      setLoading(true);
      try {
        const response = await postData(
          isOverall ? '/reports/instant-multi-report' : '/reports/instant',
          body,
        );

        // In case of timeout or any other error
        if (response.status !== 201) {
          setMessagebar((prev: any) => ({
            ...prev,
            show: true,
            text: `${response.data}`,
            severity: 'error',
          }));
          return;
        }

        setPerOrganisationBreakdown(response.data.per_organisation || []);

        if (
          !response.data.chart_data
          || Object.keys(response.data.chart_data).length === 0
        ) {
          setChartData(response.data);
          setChartKey((prev) => prev + 1);
          return;
        }

        setChartData(response.data);
        setChartKey((prev) => prev + 1);

        const chartDataByActivity = response.data.chart_data.chart_data_by_activity;
        const valuesArrayActivityChart = chartDataByActivity.datasets[0].data;
        const valuesArrayTimeSeries = chartDataByActivity.datasets[0].data;

        // Calculate total emission
        setTotalEmissionActivityChart(
          valuesArrayActivityChart
            .reduce((a: number, b: number) => a + b, 0)
            .toFixed(2),
        );
        setTotalEmissionTimeSeries(
          valuesArrayTimeSeries
            .reduce((a: number, b: number) => a + b, 0)
            .toFixed(2),
        );
      } catch (error: any) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: `${error?.message || 'Unable to fetch report data'}`,
          severity: 'error',
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedOrganisation,
    reportTypes,
    calenderStartDate,
    calenderEndDate,
    ifAggregate,
  ]);

  useEffect(() => {
    const updateReportSecond = async () => {
      if (reportEditMode.id !== '') {
        const body = {
          name: reportEditMode.name,
          description: reportEditMode.description,
          organisation: selectedOrganisation,
          report_types: reportTypesEditMode,
          expiry: reportEditMode.expiry,
          start_date: formatISO(calanderStartDateEditMode),
          end_date: formatISO(calanderEndDateEditMode),
        };
        // Trigger udpate the reportEditMode with new changes
        let getUpdatedReport;
        try {
          getUpdatedReport = await patchData(
            `/reports/${reportEditMode.id}`,
            body,
          );
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error(error);
        }

        if (getUpdatedReport.status !== 404) {
          try {
            const listUrl = await getReportListUrl();
            const getAllReports = await getData(listUrl);
            const allReports = Array.isArray(getAllReports.data) ? getAllReports.data : [];
            setReports(allReports);
            const reportEditModeSecond = getUpdatedReport.data;
            const singleReport = allReports.filter(
              (single: any) => single.id === reportEditModeSecond.id,
            );
            setReportEditMode(singleReport[0]);
          } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
      }
    };
    updateReportSecond();
  }, [calanderStartDateEditMode, calanderEndDateEditMode, reportTypesEditMode]);

  // Default the location filter to "everything selected" whenever the available
  // locations change (new locations loaded, or the org list finishes fetching).
  useEffect(() => {
    setSelectedLocationIds(locations.map((loc) => loc.id));
    setAppliedTags([]);
    setIsolatedLocationId(null);
  }, [locations]);

  const filteredBreakdown = perOrganisationBreakdown.filter(
    (org) => selectedLocationIds.includes(org.organisation_id),
  );

  // One unique colour per location, so the chart legend, the stacked segments
  // and the breakdown row dots all agree. Derived from the location's index
  // within the CURRENT selection so the selected locations always span the full
  // gradient (light teal → purple) and never collapse to near-identical shades.
  const locationColour = (organisationId: string) => {
    const index = filteredBreakdown.findIndex(
      (org) => org.organisation_id === organisationId,
    );
    return brandColour(index, Math.max(filteredBreakdown.length, 1));
  };

  const breakdownGrandTotal = filteredBreakdown.reduce(
    (sum, org) => sum + org.total_emissions,
    0,
  );

  // Tags currently applied that this location is a member of — shown as chips
  // on its breakdown row, mirroring the prototype.
  const appliedTagsForLocation = (organisationId: string) => {
    const matched = locations.find((loc) => loc.id === organisationId);
    return (matched?.tags || []).filter((tag) => appliedTags.includes(tag));
  };

  // Activity chart stacked by location: one dataset per selected location,
  // built from per_organisation.activity_emissions. Falls back to an empty
  // dataset list when the API response predates that field, in which case the
  // caller keeps using the merged single-series chart.
  const stackedBarChartData = () => {
    const labels = chartData?.chart_data?.chart_data_by_activity?.labels || [];
    const datasets = filteredBreakdown.map((org) => {
      const rgb = locationColour(org.organisation_id);
      const dimmed = isolatedLocationId !== null && isolatedLocationId !== org.organisation_id;
      return {
        label: org.name,
        // Coerce to a finite number so bad/missing API values render as 0
        // rather than breaking the stack.
        data: labels.map((label: string) => {
          const value = Number(org.activity_emissions?.[label] ?? 0);
          return Number.isFinite(value) ? value : 0;
        }),
        backgroundColor: `rgba(${rgb},${dimmed ? 0.16 : 0.9})`,
        // Round the OUTER edges of the whole column, not every segment: a fixed
        // per-segment radius can't render inside 2-5px slivers and looks broken
        // in skewed data. stackCornerRadius rounds only the top of the topmost
        // segment and the bottom of the bottommost segment in each column; the
        // thin white border keeps locations visually separated inside the stack.
        borderColor: '#fff',
        borderWidth: 1,
        borderRadius: stackCornerRadius,
        borderSkipped: false as const,
        barThickness: 'flex' as const,
        maxBarThickness: 58,
        categoryPercentage: 0.72,
        barPercentage: 0.92,
      };
    });
    return { labels, datasets };
  };

  const hasPerLocationActivityData = filteredBreakdown.some(
    (org) => org.activity_emissions && Object.keys(org.activity_emissions).length > 0,
  );

  const toggleIsolatedLocation = (organisationId: string) => {
    setIsolatedLocationId(
      (prev) => (prev === organisationId ? null : organisationId),
    );
  };

  // CSV of the current selection: a commented metadata block (who/what/when,
  // matching the PDF report header) followed by one row per location/activity.
  const exportBreakdownCsv = () => {
    if (filteredBreakdown.length === 0) return;
    const labels: string[] = chartData?.chart_data?.chart_data_by_activity?.labels || [];
    const allSelected = filteredBreakdown.length === locations.length;
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;

    let out = '';
    out += '# Evalue8 Sustainability — Emissions Report\n';
    out += `# Generated: ${new Date().toISOString()}\n`;
    out += '# View: Overall Total\n';
    out += `# Chart: ${chartType === 'activityChart' ? 'Activity chart' : 'Time series chart'}\n`;
    out += `# Tags applied: ${appliedTags.length ? appliedTags.join(', ') : 'None'}\n`;
    out += `# Locations included: ${allSelected ? 'All' : filteredBreakdown.map((org) => org.name).join(', ')}\n`;
    out += `# Start date: ${format(new Date(calenderStartDate), 'yyyy-MM-dd')}\n`;
    out += `# End date: ${format(new Date(calenderEndDate), 'yyyy-MM-dd')}\n`;
    out += '# Units: tCO2e\n#\n';
    out += 'Location,Tags,Activity,CO2 Emissions (tCO2e)\n';

    filteredBreakdown.forEach((org) => {
      const matched = locations.find((loc) => loc.id === org.organisation_id);
      const tags = (matched?.tags || []).join('; ');
      if (labels.length > 0 && org.activity_emissions) {
        labels.forEach((label) => {
          const value = org.activity_emissions?.[label] || 0;
          out += `${escape(org.name)},${escape(tags)},${escape(label)},${value.toFixed(2)}\n`;
        });
      } else {
        out += `${escape(org.name)},${escape(tags)},"All activities",${org.total_emissions.toFixed(2)}\n`;
      }
    });

    const blob = new Blob([`\uFEFF${out}`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `evalue8-report_${format(new Date(calenderStartDate), 'yyyy-MM-dd')}_${format(new Date(calenderEndDate), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const showLocationFilter = isOverallTotal && locations.length > 0;
  const filterLocations = locations.map((loc) => ({
    id: loc.id,
    name: loc.name,
    tags: loc.tags || [],
  }));
  const noLocationsSelected = isOverallTotal && selectedLocationIds.length === 0;

  const handleFilterChange = (next: { selectedIds: string[]; appliedTags: string[] }) => {
    setSelectedLocationIds(next.selectedIds);
    setAppliedTags(next.appliedTags);
    if (isolatedLocationId && !next.selectedIds.includes(isolatedLocationId)) {
      setIsolatedLocationId(null);
    }
  };

  // Keep the charts (not just the Location Breakdown table) in sync with the
  // location/tag filter while viewing Overall Total. This also doubles as the
  // recovery path when the main fetch effect skipped itself because `locations`
  // hadn't loaded yet on a refresh — once locations arrive, breakdownSelectedIds
  // updates (see the effect above) and this fetch fires with the real list.
  useEffect(() => {
    if (!isOverallTotal || !initialLoadRef.current) return;

    const filteredLocationIds = selectedLocationIds;

    if (filteredLocationIds.length === 0) return;

    const fetchFilteredChartData = async () => {
      setLoading(true);
      try {
        const body = {
          organisation: filteredLocationIds,
          report_types: reportTypes,
          start_date: formatISO(calenderStartDate),
          end_date: formatISO(calenderEndDate),
          aggregate: true,
          use_currency_conversion: true,
        };
        const response = await postData('/reports/instant-multi-report', body);
        if (response.status !== 201) return;

        setChartData(response.data);
        setPerOrganisationBreakdown(response.data.per_organisation || []);
        setChartKey((prev) => prev + 1);

        const chartDataByActivity = response.data?.chart_data?.chart_data_by_activity;
        const valuesArray = chartDataByActivity?.datasets?.[0]?.data || [];
        const total = valuesArray.reduce((a: number, b: number) => a + b, 0).toFixed(2);
        setTotalEmissionActivityChart(total);
        setTotalEmissionTimeSeries(total);
      } catch (error: any) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: `${error?.message || 'Unable to fetch filtered report data'}`,
          severity: 'error',
        }));
      } finally {
        setLoading(false);
      }
    };
    fetchFilteredChartData();
  }, [selectedLocationIds]);

  return (
    <Card
      sx={{
        margin: '20px',
        borderRadius: '20px',
        padding: '40px',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
        opacity: 1,
        filter: 'brightness(1.05)',
        minHeight: 'calc(100% - 50px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HamburgerIcon />
      {orgActivityStartDate === new Date() && <div />}
      {orgActivityStartDate !== new Date() && (
        <>
          <FirstLine
            tabValues={firstTabLinks}
            handleTab={handleFirstTabLinks}
            handleDropdown={handleChartType}
            filterSlot={showLocationFilter ? (
              <LocationTagFilter
                locations={filterLocations}
                selectedIds={selectedLocationIds}
                appliedTags={appliedTags}
                onChange={handleFilterChange}
              />
            ) : null}
            tagSlot={showLocationFilter ? (
              <LocationTagFilter
                section="tags"
                locations={filterLocations}
                selectedIds={selectedLocationIds}
                appliedTags={appliedTags}
                onChange={handleFilterChange}
              />
            ) : null}
            actions={(
              <>
                {isOverallTotal && (
                  <Button
                    onClick={exportBreakdownCsv}
                    disabled={filteredBreakdown.length === 0}
                    sx={toolbarButtonSx}
                  >
                    Export CSV
                  </Button>
                )}
                <Button
                  onClick={() => handleDownloadbutton()}
                  sx={toolbarPrimaryButtonSx}
                >
                  Download report
                </Button>
              </>
            )}
          />
          <SecondLine
            orgActivityStartDate={orgActivityStartDate}
            calanderStartDate={calenderStartDate}
            handleStartDate={handleSetStartDate}
            calanderEndDate={calenderEndDate}
            handleEndDate={handleSetEndDate}
            sliderValue={sliderValue}
            handleSliderChange={handleSliderChange}
            handleSliderLabel={handleSliderLabel}
            tabValues={secondTabLinks}
            handleTab={handleSecondTabLinks}
          />
          {/* Render diagrams */}
          <div className="thirdline">
            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                padding: '22px 26px 18px',
                display: chartType === 'timeSeriesChart' ? 'block' : 'none',
              }}
            >
              <Box
                sx={{ height: '440px' }}
              >
                {!noLocationsSelected && chartData?.chart_data?.chart_data_by_activity?.labels ? (
                  <Line
                    key={`line-${chartKey}`}
                    options={lineChartOptions}
                    data={processDataForLineChart(chartData)}
                  />
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="500px"
                    width="100%"
                    fontSize="xx-large"
                    fontWeight="bold"
                  >
                    <Typography color="textSecondary">
                      {noLocationsSelected
                        ? 'Select at least one location, or click a tag, to see data.'
                        : 'No data available for selected organisation or filter.'}
                    </Typography>
                  </Box>
                )}
              </Box>
              {!noLocationsSelected && chartData?.chart_data?.chart_data_by_activity?.labels ? (
                <Box sx={{ textAlign: 'center', color: 'rgb(100,100,100)' }}>
                  <b>
                    Total Emissions (tCO2-e):
                    {' '}
                    {formatTonnes(totalEmissionTimeSeries)}
                  </b>
                </Box>
              ) : (
                ''
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                padding: '22px 26px 18px',
                display: chartType === 'activityChart' ? 'block' : 'none',
              }}
            >
              <Box
                sx={{ height: { xs: '380px', md: '440px' }, position: 'relative' }}
              >
                {!noLocationsSelected && chartData?.chart_data?.chart_data_by_activity?.labels ? (
                  <Bar
                    key={`bar-${chartKey}-${isolatedLocationId || 'all'}`}
                    options={
                      isOverallTotal && hasPerLocationActivityData
                        ? stackedBarChartOptions
                        : barChartOptions
                    }
                    data={
                      isOverallTotal && hasPerLocationActivityData
                        ? stackedBarChartData()
                        : processDataForBarChart(chartData)
                    }
                    plugins={
                      isOverallTotal && hasPerLocationActivityData
                        ? stackedBarChartPlugins
                        : []
                    }
                  />
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="500px"
                    width="100%"
                    fontSize="xx-large"
                    fontWeight="bold"
                  >
                    <Typography color="textSecondary">
                      {noLocationsSelected
                        ? 'Select at least one location, or click a tag, to see data.'
                        : 'No data available for selected organisation or filter.'}
                    </Typography>
                  </Box>
                )}
              </Box>
              {!noLocationsSelected && chartData?.chart_data?.chart_data_by_activity?.labels ? (
                <Box sx={{ textAlign: 'center', color: 'rgb(100,100,100)' }}>
                  <b>
                    Total Emissions (tCO2-e):
                    {' '}
                    {formatTonnes(totalEmissionActivityChart)}
                  </b>
                </Box>
              ) : (
                ''
              )}
            </Paper>

            {isOverallTotal && perOrganisationBreakdown.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  padding: '22px 26px',
                  marginTop: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    gap: '16px',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                      Location breakdown
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6b7478' }}>
                      Same selection as the chart. Click a row to highlight that
                      location's segments above.
                    </Typography>
                  </Box>
                  {isolatedLocationId && (
                    <Button
                      onClick={() => setIsolatedLocationId(null)}
                      sx={{ textTransform: 'none', color: '#4a9aa3', fontSize: '13px' }}
                    >
                      Show all
                    </Button>
                  )}
                </Box>
                <TableContainer>
                  <Table aria-label="location breakdown table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={breakdownHeadSx}>Location</TableCell>
                        <TableCell sx={breakdownHeadSx}>Share</TableCell>
                        <TableCell sx={{ ...breakdownHeadSx, textAlign: 'right' }}>
                          CO₂ (Tonnes)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBreakdown.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ ...breakdownCellSx, color: '#9aa3a7' }}>
                            Nothing selected — the table mirrors the chart above.
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredBreakdown.map((org) => {
                        const percentage = breakdownGrandTotal > 0
                          ? ((org.total_emissions / breakdownGrandTotal) * 100)
                          : 0;
                        const rgb = locationColour(org.organisation_id);
                        const rowTags = appliedTagsForLocation(org.organisation_id);
                        return (
                          <TableRow
                            key={org.organisation_id}
                            hover
                            onClick={() => toggleIsolatedLocation(org.organisation_id)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor:
                                isolatedLocationId === org.organisation_id
                                  ? '#e3f5f3'
                                  : 'transparent',
                            }}
                          >
                            <TableCell sx={breakdownCellSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box
                                  sx={{
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    backgroundColor: `rgb(${rgb})`,
                                    flexShrink: 0,
                                  }}
                                />
                                {org.name}
                                {rowTags.map((tag: string) => (
                                  <Chip
                                    key={`${org.organisation_id}-${tag}`}
                                    label={tag}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#eeedfe',
                                      color: '#3c3489',
                                      fontSize: '11px',
                                      height: '20px',
                                    }}
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell sx={breakdownCellSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box
                                  sx={{
                                    backgroundColor: '#eef2f2',
                                    borderRadius: '3px',
                                    height: 6,
                                    width: 150,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${percentage.toFixed(1)}%`,
                                      backgroundColor: `rgb(${rgb})`,
                                      height: 6,
                                      borderRadius: '3px',
                                    }}
                                  />
                                </Box>
                                {percentage.toFixed(1)}
                                %
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...breakdownCellSx, textAlign: 'right' }}>
                              {org.total_emissions.toLocaleString('en-AU', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </div>
        </>
      )}

      {/* Welcome Dialog */}
      <DialogBox show={showWelcome} setShow={setShowWelcome}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="card-title" style={{ fontSize: '30px' }}>
            Welcome to your dashboard!
          </div>
          <br />
          <p>
            This is where you view your carbon emissions, how they compare to
            industry benchmarks, and share or download reports.
          </p>
          <p>
            {' '}
            You do not have any activities set up yet. Start by creating a new
            activity set at the inventory page by clicking on 'Your Inventory'
            below!
          </p>

          <Button
            sx={{ color: 'rgb(100, 100, 100)' }}
            size="large"
            startIcon={<i className="fas fa-file-alt" />}
            onClick={() => navigate('/inventory')}
          >
            Your inventory
          </Button>
        </div>
      </DialogBox>

      {/* Share Dialog */}
      <DialogBox show={showShare} setShow={setShowShare}>
        <div style={{ padding: '50px' }}>
          <div className="card-title" style={{ fontSize: '30px' }}>
            Share a report
          </div>
          <div style={{ textAlign: 'right' }}>
            <Button
              sx={{ color: 'rgb(100, 100, 100)' }}
              size="large"
              startIcon={<DashboardCustomizeIcon className="start-icons" />}
              onClick={() => handleCreateNewReport()}
            >
              Create new report
            </Button>
          </div>
          <TableContainer component={Paper} sx={{ margin: '20px 0' }}>
            <Table aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Report Name</StyledTableCell>
                  <StyledTableCell align="right">Expires On</StyledTableCell>
                  <StyledTableCell align="right">Created On</StyledTableCell>
                  <StyledTableCell align="right">Report Start Data - End Date</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length > 0
                  && reports.map((report: any) => (
                    <StyledTableRow key={report.id}>
                      <StyledTableCell
                        align="right"
                        sx={{
                          color: '#61b9c3',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => shareAReport(report)}
                      >
                        <Box>{report.name}</Box>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {format(new Date(report.expiry), 'MMMM d, yyyy')}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {format(new Date(report.created_at), 'MMMM d, yyyy')}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {`${format(new Date(report.start_date), 'MMMM d, yyyy')} - ${format(new Date(report.end_date), 'MMMM d, yyyy')}`}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <i
                          style={{ cursor: 'pointer', margin: '0 5px' }}
                          className="fas fa-share-alt"
                          onClick={() => shareAReport(report)}
                        />
                        <i
                          style={{ cursor: 'pointer', margin: '0 5px' }}
                          className="fas fa-trash"
                          onClick={() => confirmDeleteAReport(report)}
                        />
                        <i
                          style={{ cursor: 'pointer', margin: '0 5px' }}
                          className="fas fa-pencil-alt"
                          onClick={() => editAReport(report)}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </DialogBox>

      {/* Create new report Dialog */}
      <Dialog
        open={showCreateNewReport}
        onClose={() => setShowCreateNewReport(false)}
        PaperProps={{ style: { borderRadius: '10px' } }}
      >
        <div style={{ padding: '50px' }}>
          <div className="card-title" style={{ fontSize: '30px', textAlign: 'center' }}>
            Create a new report
          </div>
          <br />
          <form onSubmit={handleSubmitCreateNewReport}>
            <FormField
              error={defaultNewReportErrors.nameError}
              label="Report name"
              type="text"
              handleField={(e) => setNewReportValues((prevState) => ({
                ...prevState,
                name: e.target.value,
              }))}
            />
            <FormField
              error={defaultNewReportErrors.descriptionError}
              label="Report description"
              type="textarea"
              handleField={(e) => setNewReportValues((prevState) => ({
                ...prevState,
                description: e.target.value,
              }))}
            />
            <br />
            <ButtonFilled handleSubmit={() => handleSubmitCreateNewReport}>
              Submit
            </ButtonFilled>
          </form>
        </div>
      </Dialog>

      {/* Edit report dialog */}
      <DialogBox show={showEditReport} setShow={setShowEditReport}>
        <div style={{ padding: '50px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '50px' }}>
            <div className="card-title" style={{ fontSize: '30px' }}>
              Edit a report
            </div>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                style={{
                  outline: 'none',
                  border: '1px solid transparent',
                  borderRight: 'none',
                  borderRadius: '5px 0 0 5px',
                  padding: '3px',
                  paddingLeft: '9px',
                  width: '200px',
                  backgroundColor: '#e3ecef',
                  fontWeight: '300',
                }}
                type="text"
                value={reportEditMode.name ? reportEditMode.name : ''}
                placeholder={reportEditMode.name}
                name="reportName"
                onChange={(e: any) => setReportEditMode((prev: any) => ({
                  ...prev,
                  name: e.target.value,
                }))}
              />
              <i
                style={{
                  border: '1px solid transparent',
                  borderLeft: 'none',
                  borderRadius: '0 5px 5px 0',
                  padding: '7px',
                  backgroundColor: '#e3ecef',
                }}
                className="fa fa-pencil"
              />
            </Box>
          </Box>

          <FirstLine
            tabValues={firstTabLinksEditMode}
            handleTab={handleFirstTabLinksEditMode}
            handleDropdown={handleChartTypeEditMode}
            reportExpiry={reportEditMode.expiry}
          />

          <Box
            sx={{
              display: { xs: 'block', md: 'flex' },
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                sx={{
                  color: 'rgb(100, 100, 100)',
                  cursor: 'default !important',
                }}
                size="large"
                startIcon={<CalendarTodayIcon className="start-icons" />}
              >
                Start date
              </Button>
              <span>
                <DatePicker
                  minDate={orgActivityStartDate}
                  maxDate={subDays(new Date(), 1)}
                  selected={calanderStartDateEditMode}
                  dateFormat="dd/MM/yyyy"
                  onChange={(date: any) => handleSetStartDateEditMode(date)}
                  className="w-100 outline-0 cursor-pointer"
                />
              </span>
            </Box>
            <Box sx={{ display: { xs: 'flex' }, alignItems: 'center' }}>
              <Button
                sx={{
                  color: 'rgb(100, 100, 100)',
                  cursor: 'default !important',
                }}
                size="large"
                startIcon={<CalendarTodayIcon className="start-icons" />}
              >
                End date
              </Button>
              <span>
                <DatePicker
                  minDate={addDays(calanderStartDateEditMode, 1)}
                  maxDate={new Date()}
                  todayButton="Today"
                  selected={calanderEndDateEditMode}
                  dateFormat="dd/MM/yyyy"
                  onChange={(date: any) => handleSetEndDateEditMode(date)}
                  className="w-100 outline-0 cursor-pointer"
                />
              </span>
            </Box>
          </Box>
          <br />
          {chartTypeEditMode === 'timeSeriesChart' && (
            <Paper sx={{ padding: '5%', borderRadius: '20px' }}>
              <Line
                options={lineChartOptions}
                data={processDataForLineChart(reportEditMode)}
                height={500}
              />
            </Paper>
          )}
          {chartTypeEditMode === 'activityChart' && (
            <Paper sx={{ padding: '5%', borderRadius: '20px' }}>
              <Bar
                options={barChartOptions}
                data={processDataForBarChart(reportEditMode)}
                height={500}
              />
            </Paper>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                marginTop: '20px',
                width: '450px',
                display: 'flex',
                gap: '20px',
              }}
            >
              <ButtonFilled handleSubmit={() => cancelEditReport()}>
                Cancel
              </ButtonFilled>
              <ButtonFilled handleSubmit={() => handleSaveChanges()}>
                Save Changes
              </ButtonFilled>
            </div>
          </Box>
        </div>
      </DialogBox>

      {/* Download Dialog */}
      <Dialog
        open={showDownload}
        onClose={() => setShowDownload(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
            paddingRight: 2,
          }}
        >
          <span style={{ fontSize: '24px', fontWeight: 500 }}>Download a report</span>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleCreateNewReport}
              sx={{ textTransform: 'none', fontWeight: 'bold', color: '#6b6b6b' }}
              startIcon={<DashboardCustomizeIcon />}
            >
              CREATE NEW REPORT
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <i
              className="fas fa-times"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowDownload(false)}
            />
          </Box>
        </DialogTitle>

        {reports.length === 0 ? (
          <Typography sx={{
            textAlign: 'center', py: 5, color: '#999', fontSize: '1rem',
          }}
          >
            No saved reports found. Please create a new report.
          </Typography>
        ) : (
          <DialogContent dividers>
            <TableContainer component={Paper} sx={{ margin: '20px 0' }}>
              <Table aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Report Name</StyledTableCell>
                    <StyledTableCell align="right">Created On</StyledTableCell>
                    <StyledTableCell align="right">Report Start Data - End Date</StyledTableCell>
                    <StyledTableCell align="right">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length > 0
                    && reports.map((report: any) => (
                      <StyledTableRow key={report.id}>
                        <StyledTableCell
                          align="right"
                          sx={{
                            color: '#61b9c3',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => downloadAReport(report)}
                        >
                          {report.name}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {format(new Date(report.created_at), 'MMMM d, yyyy')}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {`${format(new Date(report.start_date), 'MMMM d, yyyy')} - ${format(new Date(report.end_date), 'MMMM d, yyyy')}`}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <i
                            style={{ cursor: 'pointer', margin: '0 5px' }}
                            className="fas fa-download"
                            onClick={() => downloadAReport(report)}
                          />
                          <i
                            style={{ cursor: 'pointer', margin: '0 5px' }}
                            className="fas fa-trash"
                            onClick={() => confirmDeleteAReport(report)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        )}
      </Dialog>

      {/* Confirm YES/NO Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{
          style: {
            borderRadius: '10px',
          },
        }}
      >
        <div style={{ padding: '50px' }}>
          <div
            className="card-title"
            style={{ fontSize: '30px', textAlign: 'center' }}
          >
            Are you sure you want to delete?
          </div>
          <br />
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <Chip
              icon={<CloseIcon style={{ fill: '#fff' }} />}
              label="Cancel"
              size="small"
              sx={{ cursor: 'pointer', backgroundColor: '#f55', color: '#fff' }}
              onClick={() => setShowConfirmDialog(false)}
            />
            <Chip
              icon={<DoneIcon style={{ fill: '#fff' }} />}
              label="Confirm"
              size="small"
              sx={{
                cursor: 'pointer',
                backgroundColor: '#61b9c3',
                color: '#fff',
              }}
              onClick={() => deleteAReport()}
            />
          </Box>
        </div>
      </Dialog>

      {/* Download single Report Dialog */}
      <DialogBox show={showDownloadReport} setShow={setShowDownloadReport}>
        <div style={{ padding: '50px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              Report generation process might take around 30 seconds to
              complete.
              {' '}
              <br />
              {' '}
              <br />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  marginTop: '20px',
                  width: '450px',
                  display: 'flex',
                  gap: '20px',
                }}
              >
                <ButtonFilled handleSubmit={() => cancelDownloadReport()}>
                  Cancel
                </ButtonFilled>
                <ButtonFilled handleSubmit={() => downloadNow()}>
                  Download
                </ButtonFilled>
              </div>
            </Box>
          </Box>
        </div>
      </DialogBox>

      {/* Generate Share code  */}
      <Dialog
        open={showGenerateCode}
        onClose={() => setShowGenerateCode(false)}
        PaperProps={{
          style: {
            borderRadius: '10px',
          },
        }}
      >
        <div style={{ padding: '50px' }}>
          <div
            className="card-title"
            style={{ fontSize: '30px', textAlign: 'center' }}
          >
            Create a share code
          </div>
          <br />
          <form onSubmit={handleCreateShareCode}>
            <div style={{ fontSize: '13px', color: '#61b9c3' }}>
              This Share code should expire on
            </div>
            <div style={{ textAlign: 'center' }}>
              <DatePicker
                inline
                selected={newReportValues.expiry}
                dateFormat="dd/MM/yyyy"
                minDate={addDays(new Date(), 1)}
                onChange={(date: any) => setShareCodeExpiry(date)}
                className="w-100 outline-0 cursor-pointer"
              />
            </div>
            <br />
            <ButtonFilled handleSubmit={() => handleCreateShareCode}>
              Submit
            </ButtonFilled>
            {/* <button>Submit</button> */}
          </form>
        </div>
      </Dialog>

      {/* Display Share code  */}
      <Dialog
        open={showShareCode}
        onClose={() => setShowShareCode(false)}
        PaperProps={{
          style: {
            borderRadius: '10px',
          },
        }}
      >
        <div style={{ padding: '50px' }}>
          <div
            className="card-title"
            style={{ fontSize: '30px', textAlign: 'center' }}
          >
            Share this link
          </div>

          <a
            target="_blank"
            rel="noreferrer"
            href={`https://www.evalue8.net/${newShareCode}`}
          >
            https://www.evalue8.net/
            {newShareCode}
          </a>
        </div>
      </Dialog>

      {/* This dialogbox is for loading the Line and Bar component for ref purpose */}
      <DialogBox show={false} setShow={setShowDownload}>
        <Line
          options={lineChartOptions}
          data={processDataForLineChart(reportDownloadMode)}
          height={500}
          ref={lineChartRef}
        />
        <Bar
          options={barChartOptions}
          data={processDataForBarChart(reportDownloadMode)}
          height={500}
          ref={barChartRef}
        />
      </DialogBox>
    </Card>
  );
}

export default Dashboard;
